import React, { useMemo } from "react";

import { useSelector } from "react-redux";
import { Field, FormSection, getFormValues } from "redux-form";
import {
	ReduxControls,
	ErrorBanner
} from "rhombus-react";

import DynamicAssetSelect from "social/DynamicAssetSelect";
import { isDynamic } from "social/DynamicCreativeToggle";
import { assetType } from "social/BuildMethodRadio";
import DynamicInput from "social/DynamicInput";
import DynamicSelect from "social/DynamicSelect";
import { UNEDITABLE_DYNAMIC_VARIABLE_MESSAGE } from "social/constants/common";

import { getErrorMessagesByAdType } from "selectors/Social2";

import { SNAPCHAT_CTAS } from "social/constants/CTA";
import {
	prettifyMessages,
	mapStringToOption,
	dynamicMapStringToOption,
	dynamicNormalizeOptionToString,
	nonDynamicFields, isRequiredAssetField
} from "social/utils";
import { CREATIVE_NAME_MAX_LENGTH } from "widgets/forms/validators/Creatives";
import { SNAPCHAT_VIDEO_CONSTRAINTS } from "social/validators/snapchat";

import { ASSET_TYPES, SOCIAL_AD_TYPES } from "utils/constants";
import { formatBytes } from "utils/common";
import { SOCIAL_WIZARD } from "widgets/forms/list";
import SnapchatDisclaimer from "social/SnapchatDisclaimer";
import { imposiumCompositionWithoutProjectName } from "social/ImposiumCompSelector";
import { imposiumProject } from "social/ImposiumProjectSelector";
import { afterEffectsPackage } from "social/AfterEffectsPackageSelector";
import { afterEffectsComposition } from "social/AfterEffectsCompSelector";

const {
	MAX_LENGTH_HEADLINE,
	MAX_LENGTH_BRAND_NAME,
	MAX_LENGTH_LANDING_PAGE,
	MIN_VIDEO_SEC,
	MAX_VIDEO_MIN,
	MAX_VIDEO_BYTES,
	VIDEO_ACCEPT,
	DIMENSIONS,
} = SNAPCHAT_VIDEO_CONSTRAINTS;

export const SNAPCHAT_VIDEO_FIELDS = {
	isDynamic,
	assetType,
	imposiumProject,
	imposiumComposition: imposiumCompositionWithoutProjectName,
	afterEffectsPackage,
	afterEffectsComposition,
	video: {
		name: "video",
		label: "Video",
		className: "full-width",
		component: DynamicAssetSelect,
		defaultDynVarName: "video",
		assetType: ASSET_TYPES.VIDEO,
		accept: VIDEO_ACCEPT,
		required: true,
		tip: {
			direction: "top",
			description: `
				To prevent overlap, avoid placement of elements within 150px from the top<br />
				and bottom and 5% of the left and right sides of the creative. An 'AD' slug<br />
				is added on the lower right corner.
			`,
			html: true,
		},
		requirements: [
			{
				name: "File Size",
				description: `${formatBytes(MAX_VIDEO_BYTES)} Maximum`,
			},
			{
				name: "File Types",
				description: `${VIDEO_ACCEPT} (all files must be H.264 encoded)`,
			},
			{
				name: "Dimensions",
				description: `${DIMENSIONS.width}x${DIMENSIONS.height} pixels`,
			},
			{
				name: "Duration",
				description: `${MIN_VIDEO_SEC}s - ${MAX_VIDEO_MIN}m`,
			}
		],
	},
	name: {
		name: "name",
		label: "Creative Name",
		className: "full-width",
		component: ReduxControls.Input,
		required: true,
		placeholder: "Enter a creative name",
		charMax: CREATIVE_NAME_MAX_LENGTH,
		showCharCount: true,
		showPristineErrors: true,
	},
	headline: {
		name: "headline",
		label: "Headline",
		className: "full-width",
		component: DynamicInput,
		defaultDynVarName: "headline",
		required: true,
		placeholder: "Enter a short headline",
		charMax: MAX_LENGTH_HEADLINE,
		// showCharCount: true,
	},
	brandName: {
		name: "brandName",
		label: "Brand Name",
		className: "full-width",
		component: DynamicInput,
		defaultDynVarName: "brand_name",
		placeholder: "Enter your brand name",
		charMax: MAX_LENGTH_BRAND_NAME,
		// showCharCount: true,
		tip: {
			direction: "right",
			description: "Brand name must match the paying Advertiser whose products or services are being advertised",
		},
	},
	landingPage: {
		name: "landingPage",
		label: "Landing Page",
		className: "full-width",
		component: DynamicInput,
		defaultDynVarName: "clickTag1",
		charMax: MAX_LENGTH_LANDING_PAGE,
		// showCharCount: true,
		placeholder: "https://www.example.com",
		required: true,
	},
	disclaimerText: {
		name: "disclaimerEnabled",
		component: SnapchatDisclaimer,
	},
	cta: {
		name: "cta",
		label: "CTA",
		className: "half-width",
		component: DynamicSelect,
		defaultDynVarName: "cta",
		options: SNAPCHAT_CTAS.map(mapStringToOption),
		format: dynamicMapStringToOption,
		normalize: dynamicNormalizeOptionToString,
		required: true,
		menuPlacement: "top",
		placeholder: "Select Call to Action Text",
		isClearable: false,
		variableNameInputTip: "Edit variable name",
	},
};

const SNAPCHAT_VIDEO_SETTINGS = [
	{
		name: "blockPreloading",
		label: "Block Web View Preloading",
		className: "setting-checkbox",
		component: ReduxControls.Checkbox,
		useValue: true,
		...nonDynamicFields(),
	},
	{
		name: "allowShare",
		label: "Allow Users to Share with Friends",
		className: "setting-checkbox",
		component: ReduxControls.Checkbox,
		useValue: true,
		...nonDynamicFields(),
	},
];

const adType = SOCIAL_AD_TYPES.SNAPCHAT_VIDEO;

export default function SnapchatVideo(props){
	const { navigationActions, libraryId, isViewOnly, showPristineErrors, doVersionsExist } = props;

	const formValues = useSelector(getFormValues(SOCIAL_WIZARD)) || {};
	const { assetType } = formValues[adType] || {};

	const errorMessages = useSelector(getErrorMessagesByAdType(adType, showPristineErrors));
	const prettyErrorMessages = useMemo(() => (
		prettifyMessages(errorMessages, SNAPCHAT_VIDEO_FIELDS)
	), [errorMessages]);
	const hasErrors = prettyErrorMessages.length;

	let errorDisplay;
	if (hasErrors) {
		errorDisplay = (
			<ErrorBanner title="Sorry, please fix the errors below" messages={prettyErrorMessages} />
		);
	}

	const $fields = Object.values(SNAPCHAT_VIDEO_FIELDS).map((fieldProps) => {
		const extraProps = {
		};

		if (fieldProps.name === "video") {
			extraProps.navigationActions = navigationActions;
			extraProps.libraryId = libraryId;
			extraProps.creativeAssetType = assetType;
			extraProps.required = isRequiredAssetField(assetType);
		}

		extraProps.disabled = isViewOnly;

		// If versions exist besides default, then dynamic toggle should be disabled
		if (doVersionsExist) {
			extraProps.dynamicToggleDisabled = true;
			extraProps.variableNameInputDisabled = true;
			if (fieldProps.name !== "cta") {
				extraProps.variableNameInputTip = UNEDITABLE_DYNAMIC_VARIABLE_MESSAGE;
			}
		}

		if (extraProps.disabled && fieldProps.placeholder) {
			// We set this on extraProps, because we don't want to mutate the fieldProps consts
			extraProps.placeholder = '';
		}

		extraProps.showPristineErrors = showPristineErrors;

		return	<Field key={fieldProps.name} {...fieldProps} {...extraProps} />;
	});

	return (
		<div className="form-container">
			<FormSection name={adType}>
				{errorDisplay}
				<div className="fields">
					{$fields}
					<div className="full-width e-form__control-wrapper">
						<div className="e-form__control-header">
							<label className="e-form__control-label">Settings</label>
						</div>
						<div className="settings-fields">
							{SNAPCHAT_VIDEO_SETTINGS.map(setting => (
								<Field
									key={setting.name}
									{...setting}
									className="custom-checkbox"
									disabled={isViewOnly}
								/>
							))}
						</div>
					</div>
				</div>
			</FormSection>
		</div>
	);
}

SnapchatVideo.displayName = "SnapchatVideoForm";
